import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { InputTextModule } from 'primeng/inputtext';
import { MultiSelectModule } from 'primeng/multiselect';
import { TextareaModule } from 'primeng/textarea';
import { ToastModule } from 'primeng/toast';
import {
  Qualification,
  StoreQualificationSchema,
} from '../../../../shared/models/control-points/qualification.model';
import { QualificationService } from '../../../../shared/services/control-points/qualification.service';
import { HasUnsavedChanges } from '../../../../shared/guards/unsaved-changes.guard';
import {
  applyServerErrors,
  applyZodErrors,
} from '../../../../shared/util/zod-form-errors';

@Component({
  selector: 'app-qualification-detail',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    RouterModule,
    ReactiveFormsModule,
    TranslateModule,
    CardModule,
    ButtonModule,
    InputTextModule,
    TextareaModule,
    MultiSelectModule,
    ToastModule,
    ConfirmDialogModule,
  ],
  providers: [MessageService, ConfirmationService],
  templateUrl: './qualification-detail.component.html',
  styleUrl: './qualification-detail.component.scss',
})
export class QualificationDetailComponent
  implements OnInit, HasUnsavedChanges
{
  service = inject(QualificationService);
  #messageService = inject(MessageService);
  #confirmationService = inject(ConfirmationService);
  #translate = inject(TranslateService);
  #route = inject(ActivatedRoute);
  #router = inject(Router);
  #fb = inject(FormBuilder);

  model = signal<Qualification | null>(null);
  isEditMode = signal(false);
  loading = signal(false);
  submitting = signal(false);
  #saved = signal(false);

  form: FormGroup = this.#fb.group({
    name: ['', [Validators.required, Validators.maxLength(255)]],
    description: ['' as string | null],
    user_ids: [[] as number[]],
  });

  ngOnInit(): void {
    this.service.getTrainers();
    const idParam = this.#route.snapshot.paramMap.get('id');
    if (idParam && idParam !== 'new') {
      this.isEditMode.set(true);
      this.#loadModel(Number(idParam));
    }
  }

  hasUnsavedChanges(): boolean {
    return this.form.dirty && !this.#saved();
  }

  #loadModel(id: number) {
    this.loading.set(true);
    this.service.getById(id).subscribe({
      next: (response) => {
        this.loading.set(false);
        const item = response.data;
        this.model.set(item);
        this.form.patchValue({
          name: item.name,
          description: item.description ?? '',
          user_ids: (item.users ?? [])
            .map((u: { id?: number }) => u?.id)
            .filter((id): id is number => typeof id === 'number'),
        });
        this.form.markAsPristine();
      },
      error: (e: { status?: number }) => {
        this.loading.set(false);
        if (e?.status === 404) {
          this.#messageService.add({
            severity: 'error',
            summary: this.#translate.instant('label.errorHeader'),
            detail: this.#translate.instant(
              'controlPoints.qualifications.notFound',
            ),
          });
          this.#router.navigate(['/control-points/qualifications']);
        } else {
          this.#messageService.add({
            severity: 'error',
            summary: this.#translate.instant('label.errorHeader'),
            detail: this.#translate.instant(
              'controlPoints.qualifications.loadError',
            ),
          });
        }
      },
    });
  }

  onSubmit() {
    if (this.submitting()) return;
    if (!this.form.valid) {
      this.form.markAllAsTouched();
      return;
    }
    const raw = this.form.getRawValue();
    const parsed = StoreQualificationSchema.safeParse({
      name: raw.name,
      description: raw.description?.trim() ? raw.description : null,
      user_ids: raw.user_ids ?? [],
    });
    if (!parsed.success) {
      applyZodErrors(parsed.error, this.form);
      return;
    }

    this.submitting.set(true);
    const editing = this.model();

    const call$ = editing
      ? this.service.update(editing.id, parsed.data)
      : this.service.create(parsed.data);

    call$.subscribe({
      next: (response) => {
        this.submitting.set(false);
        this.#saved.set(true);
        this.model.set(response.data);
        this.form.markAsPristine();
        this.#messageService.add({
          severity: 'success',
          summary: this.#translate.instant('label.successHeader'),
          detail: this.#translate.instant(
            editing
              ? 'controlPoints.qualifications.updated'
              : 'controlPoints.qualifications.created',
          ),
        });
        if (!editing) {
          this.isEditMode.set(true);
          this.#router.navigate([
            '/control-points/qualifications',
            response.data.id,
          ]);
        }
      },
      error: (e: unknown) => {
        this.submitting.set(false);
        applyServerErrors(e, this.form);
        this.#messageService.add({
          severity: 'error',
          summary: this.#translate.instant('label.errorHeader'),
          detail: this.#translate.instant(
            editing
              ? 'controlPoints.qualifications.updateError'
              : 'controlPoints.qualifications.createError',
          ),
        });
      },
    });
  }

  onDelete(event: Event) {
    const item = this.model();
    if (!item) return;
    this.#confirmationService.confirm({
      target: event.target as EventTarget,
      header: this.#translate.instant(
        'controlPoints.qualifications.deleteConfirmHeader',
      ),
      message: this.#translate.instant(
        'controlPoints.qualifications.deleteConfirmMessage',
      ),
      acceptLabel: this.#translate.instant('label.delete'),
      rejectLabel: this.#translate.instant('label.cancel'),
      acceptButtonProps: { severity: 'danger' },
      accept: () => {
        this.service.delete(item.id).subscribe({
          next: () => {
            this.#saved.set(true);
            this.#messageService.add({
              severity: 'success',
              summary: this.#translate.instant('label.successHeader'),
              detail: this.#translate.instant(
                'controlPoints.qualifications.deleted',
              ),
            });
            this.#router.navigate(['/control-points/qualifications']);
          },
          error: () => {
            this.#messageService.add({
              severity: 'error',
              summary: this.#translate.instant('label.errorHeader'),
              detail: this.#translate.instant(
                'controlPoints.qualifications.deleteError',
              ),
            });
          },
        });
      },
    });
  }

  onCancel() {
    this.#router.navigate(['/control-points/qualifications']);
  }
}
